import React, {
  Fragment,
  useContext,
  useEffect,
  useState,
  useRef,
} from "react";

import { ThemeProvider } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import InputAdornment from "@mui/material/InputAdornment";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";

import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import SearchIcon from "@mui/icons-material/Search";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";

import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Toast } from "primereact/toast";
import "../../../assets/css/DataTableDemo.css";
import "primereact/resources/themes/saga-blue/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import "../../../assets/css/PrimeGrid.css";

import { useNavigate } from "react-router-dom";
import { saveAs } from "file-saver";

import { AlertContext } from "../../../context/AlertContext";
import { ConfiguracionContext } from "../../../context/ConfiguracionContext";
import { AuthContext } from "../../../context/AuthContext";
import { getBrand, createBrandTheme } from "../../../theme/brand";
import Spinner from "../Spinner";
import Barra from "../Barra";

const DEFAULT_COLUMNS = [
  { field: "id", header: "ID", width: "8rem" },
  { field: "nombre", header: "Nombre" },
  { field: "habilitado", header: "Habilitado", type: "bool", width: "12rem" },
];

const DEFAULT_FIELDS = [
  { name: "nombre", label: "Nombre", type: "text", required: true },
  { name: "habilitado", label: "Habilitado", type: "switch" },
];

const DEFAULT_NUEVO = { nombre: "", habilitado: true };

/**
 * Pantalla genérica para las tablas maestras de Configuración → Archivos.
 * Encapsula barra + control de acceso + tabla + exportaciones + diálogo de alta/edición.
 */
export default function MaestroArchivo({
  titulo,
  entidad, // singular, para los mensajes ("tratamiento")
  layout, // nombre de layout para obtenerAcceso
  icono,
  items,
  onObtener, // () => Promise  (refresca la lista)
  onAgregar, // (data) => Promise
  onActualizar, // (data) => Promise
  onMount, // () => void  (carga única de listas relacionadas)
  listo = true, // gate extra: la tabla espera hasta items && listo
  permiteNuevo = true, // false → pantalla solo edición
  idKey = "id", // campo que se muestra en el recuadro "ID" del diálogo
  anchoDialog = "sm", // maxWidth del diálogo de alta/edición
  columns = DEFAULT_COLUMNS,
  fields = DEFAULT_FIELDS,
  nuevoDefault = DEFAULT_NUEVO,
  acciones = [], // botones extra en la barra: { label, icon, color, tooltip, onClick(ctx) }
  accionesFila = [], // botones por fila: { icon, tooltip, color, mostrar(row,{configuracion}), onClick(row,ctx) }
  exportColumns,
  exportName,
}) {
  const [globalFilter, setGlobalFilter] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [model, setModel] = useState(null);
  const [visible, setVisible] = useState(false);
  const [tab, setTab] = useState(0);
  const toast = useRef(null);
  const navigate = useNavigate();

  const { mensajeAlerta } = useContext(AlertContext);
  const { state } = useContext(AuthContext);
  const { statusAcceso, configuracion, obtenerAcceso } =
    useContext(ConfiguracionContext);

  const theme = createBrandTheme(getBrand(configuracion));
  const nombreEntidad = entidad || (titulo ? titulo.toLowerCase() : "registro");

  const fileName = exportName || (titulo ? titulo.toLowerCase() : "export");

  // Columnas a exportar: si viene exportColumns ({title,dataKey}) se respeta esa
  // lista, tomando el tipo/valor de la columna equivalente de la tabla.
  const columnasExport = (
    exportColumns
      ? exportColumns.map((c) => {
          const eq = columns.find((k) => k.field === c.dataKey) || {};
          return { header: c.title, field: c.dataKey, type: eq.type, value: eq.value };
        })
      : columns
  ).filter((c) => c.field);

  const celdaExport = (col, row) => {
    const raw =
      typeof col.value === "function"
        ? col.value(row, { configuracion })
        : row[col.field];
    if (col.type === "bool") return raw ? "Sí" : "No";
    return raw == null ? "" : String(raw);
  };

  useEffect(() => {
    if (state.usuario) {
      obtenerAcceso({ usuarioId: state.usuario.id, layout });
    }
    if (onMount) onMount();
    onObtener();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.id]);

  useEffect(() => {
    if (statusAcceso === 400) {
      const t = setTimeout(() => {
        mensajeAlerta(
          "Accesos",
          "No tiene permiso para acceder a esta pantalla !.",
          "error"
        );
        navigate("/");
      }, 2000);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusAcceso]);

  const exitClick = () => navigate("/");

  const enMovil = useMediaQuery("(max-width:600px)");

  const addClick = () => {
    setSelectedRow(null);
    setModel(
      typeof nuevoDefault === "function"
        ? nuevoDefault(items || [])
        : { ...nuevoDefault }
    );
    setTab(0);
    setVisible(true);
  };

  const editClick = () => {
    if (!selectedRow) {
      toast.current.show({
        severity: "info",
        summary: "Editar",
        detail: `Debe seleccionar un ${nombreEntidad}.`,
        life: 3000,
      });
      return;
    }
    setModel({ ...selectedRow });
    setTab(0);
    setVisible(true);
  };

  const onHide = () => setVisible(false);

  const aceptarClick = async () => {
    const payload = { ...model };

    for (const f of fields) {
      if (f.type === "heading") continue;

      // validación custom por campo (escape hatch)
      if (typeof f.validar === "function") {
        const err = f.validar(payload[f.name], payload);
        if (err) {
          mensajeAlerta(titulo, err, "error");
          return;
        }
      }

      if (f.type === "switch" || f.type === "number") continue;

      if (f.type === "select") {
        const v = payload[f.name];
        if (f.required && (v == null || v === "" || v <= 0)) {
          mensajeAlerta(
            titulo,
            f.requiredMsg || `El ${f.label.toLowerCase()} es necesario !.`,
            "error"
          );
          return;
        }
        continue;
      }

      // text
      const val = payload[f.name] == null ? "" : String(payload[f.name]).trim();
      payload[f.name] = val;
      if (f.required && val === "") {
        mensajeAlerta(
          titulo,
          f.requiredMsg || `El ${f.label.toLowerCase()} es necesario !.`,
          "error"
        );
        return;
      }
    }

    if (selectedRow) {
      await onActualizar(payload);
    } else {
      await onAgregar(payload);
    }

    onHide();
    setSelectedRow(null);
    await onObtener();
  };

  // --- helpers del diálogo ---
  const editando = !!selectedRow;
  const disDe = (f) =>
    typeof f.disabled === "function" ? !!f.disabled(editando) : !!f.disabled;
  // patch al modelo: si el campo define onChange(value, model) puede tocar varios campos
  const patch = (f, value) =>
    typeof f.onChange === "function"
      ? f.onChange(value, model)
      : { [f.name]: value };

  const tabsNames = [
    ...new Set(fields.filter((f) => f.tab).map((f) => f.tab)),
  ];
  const usaTabs = tabsNames.length > 0;
  const tieneFieldId = fields.some((f) => f.name === "id");

  const renderField = (f) => {
    if (f.type === "heading") {
      return (
        <Grid item xs={12} key={f.label}>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 700, mt: 1, color: "text.secondary" }}
          >
            {f.label}
          </Typography>
        </Grid>
      );
    }
    if (f.type === "switch") {
      return (
        <Grid item xs={12} sm={f.md || 12} key={f.name}>
          <FormControlLabel
            control={
              <Switch
                color="primary"
                checked={model ? !!model[f.name] : false}
                disabled={disDe(f)}
                onChange={(e) =>
                  setModel({ ...model, ...patch(f, e.target.checked) })
                }
              />
            }
            label={f.label}
          />
        </Grid>
      );
    }
    if (f.type === "select") {
      const optValue = f.optionValue || "id";
      const optLabel = f.optionLabel || "nombre";
      const opts = f.options || [];
      const current =
        opts.find((o) => o[optValue] === (model ? model[f.name] : null)) || null;
      return (
        <Grid item xs={12} sm={f.md || 12} key={f.name}>
          <Autocomplete
            options={opts}
            value={current}
            disablePortal
            disabled={disDe(f)}
            getOptionLabel={(o) => (o && o[optLabel]) || ""}
            isOptionEqualToValue={(o, v) => o[optValue] === (v && v[optValue])}
            onChange={(e, v) =>
              setModel({
                ...model,
                [f.name]: v ? v[optValue] : null,
                ...(f.labelField
                  ? { [f.labelField]: v ? v[optLabel] : null }
                  : {}),
              })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={f.label}
                required={!!f.required}
                size="small"
                fullWidth
              />
            )}
          />
        </Grid>
      );
    }
    if (f.type === "number") {
      if (f.entero) {
        const maxLen = f.maxLength || 6;
        return (
          <Grid item xs={12} sm={f.md || 12} key={f.name}>
            <TextField
              value={model && model[f.name] != null ? model[f.name] : ""}
              required={!!f.required}
              disabled={disDe(f)}
              label={f.label}
              fullWidth
              size="small"
              inputProps={{ inputMode: "numeric", maxLength: maxLen }}
              onChange={(e) =>
                setModel({
                  ...model,
                  ...patch(f, e.target.value.replace(/\D/g, "").slice(0, maxLen)),
                })
              }
            />
          </Grid>
        );
      }
      const nv = model && model[f.name] != null ? model[f.name] : "";
      const fueraRango =
        nv !== "" &&
        ((f.min != null && Number(nv) < f.min) ||
          (f.max != null && Number(nv) > f.max));
      return (
        <Grid item xs={12} sm={f.md || 12} key={f.name}>
          <TextField
            type="number"
            value={nv}
            required={!!f.required}
            disabled={disDe(f)}
            label={f.label}
            fullWidth
            size="small"
            error={fueraRango}
            helperText={fueraRango ? f.rangeMsg || "Fuera de rango" : " "}
            inputProps={{
              min: f.min,
              max: f.max,
              step: f.step != null ? f.step : "any",
            }}
            onChange={(e) =>
              setModel({ ...model, ...patch(f, e.target.value) })
            }
          />
        </Grid>
      );
    }
    // text
    return (
      <Grid item xs={12} sm={f.md || (selectedRow ? 9 : 12)} key={f.name}>
        <TextField
          value={model && model[f.name] != null ? model[f.name] : ""}
          required={!!f.required}
          disabled={disDe(f)}
          autoFocus={f === fields[0] && !disDe(f)}
          label={f.label}
          fullWidth
          size="small"
          onChange={(e) => setModel({ ...model, ...patch(f, e.target.value) })}
        />
      </Grid>
    );
  };

  const bodyTemplate = (col) => (rowData) => {
    if (typeof col.render === "function") {
      return (
        <Fragment>
          <span className="p-column-title">{col.header}</span>
          {col.render(rowData)}
        </Fragment>
      );
    }
    const raw =
      typeof col.value === "function"
        ? col.value(rowData, { configuracion })
        : rowData[col.field];
    if (col.type === "bool") {
      return (
        <Fragment>
          <span className="p-column-title">{col.header}</span>
          <Chip
            size="small"
            label={raw ? "Sí" : "No"}
            color={raw ? "success" : "default"}
            variant={raw ? "filled" : "outlined"}
          />
        </Fragment>
      );
    }
    return (
      <Fragment>
        <span className="p-column-title">{col.header}</span>
        {raw}
      </Fragment>
    );
  };

  const exportPdf = () => {
    import("jspdf").then((jsPDFmod) => {
      import("jspdf-autotable").then(() => {
        const JsPDF = jsPDFmod.jsPDF || jsPDFmod.default;
        // apaisado cuando hay muchas columnas (así entran sin aplastarse)
        const orientation = columnasExport.length > 5 ? "landscape" : "portrait";
        const doc = new JsPDF({ orientation });

        doc.setFontSize(12);
        doc.text(`Listado de ${titulo || ""}`, 14, 10);

        // Sin headStyles/theme propios: mantiene los colores por defecto de siempre.
        doc.autoTable({
          head: [columnasExport.map((c) => c.header)],
          body: (items || []).map((row) =>
            columnasExport.map((c) => celdaExport(c, row))
          ),
          startY: 16,
          styles: { overflow: "linebreak" },
        });

        window.open(doc.output("bloburl"));
      });
    });
  };

  const exportExcel = () => {
    import("xlsx").then((xlsx) => {
      const rows = (items || []).map((row) => {
        const o = {};
        columnasExport.forEach((c) => {
          o[c.header] = celdaExport(c, row);
        });
        return o;
      });
      const worksheet = xlsx.utils.json_to_sheet(rows);
      const workbook = { Sheets: { data: worksheet }, SheetNames: ["data"] };
      const excelBuffer = xlsx.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });
      const blob = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
      });
      saveAs(blob, `${fileName}_export_${new Date().getTime()}.xlsx`);
    });
  };

  const cargando = statusAcceso === 0 || statusAcceso === 400;

  return (
    <Fragment>
      <Barra />
      <Toast ref={toast} />

      {cargando ? <Spinner /> : null}

      {statusAcceso === 200 ? (
        <ThemeProvider theme={theme}>
          <Box id="dataTableBotones" sx={{ px: { xs: 1, sm: 2 }, pb: 3 }}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                mb: 2,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
              }}
            >
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={2}
                alignItems={{ xs: "stretch", md: "center" }}
              >
                <Stack
                  direction="row"
                  spacing={1.5}
                  alignItems="center"
                  sx={{ flexShrink: 0 }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 2,
                      display: "grid",
                      placeItems: "center",
                      bgcolor: "primary.main",
                      color: "primary.contrastText",
                    }}
                  >
                    {icono}
                  </Box>
                  <Box>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 700, lineHeight: 1.2 }}
                    >
                      {titulo}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {items ? `${items.length} registros` : "—"}
                    </Typography>
                  </Box>
                </Stack>

                <Box sx={{ flexGrow: 1 }} />

                <TextField
                  size="small"
                  placeholder="Buscar…"
                  onInput={(e) => setGlobalFilter(e.target.value)}
                  sx={{ minWidth: { xs: "100%", md: 240 } }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Stack>

              <Stack
                direction="row"
                spacing={1}
                flexWrap="wrap"
                useFlexGap
                sx={{ mt: 2 }}
              >
                {permiteNuevo ? (
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={addClick}
                  >
                    Nuevo
                  </Button>
                ) : null}
                <Button
                  variant="outlined"
                  startIcon={<EditOutlinedIcon />}
                  onClick={editClick}
                  disabled={!selectedRow}
                >
                  Editar
                </Button>
                {acciones.map((a) => (
                  <Button
                    key={a.label}
                    variant="outlined"
                    color={a.color || "inherit"}
                    startIcon={a.icon}
                    title={a.tooltip || a.label}
                    onClick={() =>
                      a.onClick({
                        toast: toast.current,
                        mensajeAlerta,
                        configuracion,
                        navigate,
                        items,
                        selectedRow,
                      })
                    }
                  >
                    {a.label}
                  </Button>
                ))}
                <Button
                  variant="outlined"
                  color="success"
                  startIcon={<FileDownloadOutlinedIcon />}
                  onClick={exportExcel}
                >
                  Excel
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<PictureAsPdfOutlinedIcon />}
                  onClick={exportPdf}
                >
                  PDF
                </Button>
                <Box sx={{ flexGrow: 1 }} />
                <Button
                  variant="text"
                  color="inherit"
                  startIcon={<CloseIcon />}
                  onClick={exitClick}
                >
                  Cerrar
                </Button>
              </Stack>
            </Paper>

            <Paper
              elevation={0}
              sx={{
                // el marco/redondeo lo pone .mito-grid (PrimeGrid.css) para
                // que todas las grillas de la app se vean igual
                borderRadius: 2,
                overflow: "hidden",
                // que las celdas no se corten en varias líneas; si no entran, scroll horizontal
                "& .p-datatable-wrapper": { overflowX: "auto" },
                "& .p-datatable-thead > tr > th, & .p-datatable-tbody > tr > td": {
                  whiteSpace: "nowrap",
                },
              }}
            >
              {items && listo ? (
                <DataTable
                  value={items}
                  selection={selectedRow}
                  onSelectionChange={(e) => setSelectedRow(e.value)}
                  selectionMode="single"
                  dataKey="id"
                  className="p-datatable-sm p-datatable-striped p-datatable-responsive-demo mito-grid"
                  globalFilter={globalFilter}
                  emptyMessage="No hay datos."
                  paginator
                  paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
                  currentPageReportTemplate="Mostrando {first} a {last} de {totalRecords}"
                  rows={10}
                  rowsPerPageOptions={[10, 20, 50]}
                >
                  {columns.map((col) => (
                    <Column
                      key={col.field}
                      field={col.field}
                      header={col.header}
                      body={bodyTemplate(col)}
                      sortable={col.sortable !== false}
                      style={col.width ? { width: col.width } : undefined}
                    />
                  ))}
                  {accionesFila.length ? (
                    <Column
                      header=""
                      style={{ width: `${accionesFila.length * 3 + 1}rem` }}
                      body={(row) => (
                        <Fragment>
                          {accionesFila.map((a, i) =>
                            !a.mostrar || a.mostrar(row, { configuracion }) ? (
                              <Tooltip key={i} title={a.tooltip || ""}>
                                <IconButton
                                  size="small"
                                  color={a.color || "default"}
                                  onClick={() =>
                                    a.onClick(row, {
                                      mensajeAlerta,
                                      configuracion,
                                      navigate,
                                      toast: toast.current,
                                      recargar: onObtener,
                                    })
                                  }
                                >
                                  {a.icon}
                                </IconButton>
                              </Tooltip>
                            ) : null
                          )}
                        </Fragment>
                      )}
                    />
                  ) : null}
                </DataTable>
              ) : (
                <Spinner />
              )}
            </Paper>
          </Box>

          <Dialog
            open={visible}
            onClose={onHide}
            maxWidth={anchoDialog}
            fullWidth
            fullScreen={enMovil}
          >
            <DialogTitle
              sx={{ display: "flex", alignItems: "center", gap: 1, fontWeight: 700 }}
            >
              {icono}
              {selectedRow ? `Editar ${nombreEntidad}` : `Nuevo ${nombreEntidad}`}
            </DialogTitle>
            <DialogContent dividers>
              {usaTabs ? (
                <Tabs
                  value={tab}
                  onChange={(e, i) => setTab(i)}
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{ mb: 2 }}
                >
                  {tabsNames.map((t) => (
                    <Tab key={t} label={t} />
                  ))}
                </Tabs>
              ) : null}
              <Grid container spacing={2} sx={{ mt: 0 }}>
                {(!usaTabs || tab === 0) && selectedRow && !tieneFieldId ? (
                  <Grid item xs={12} sm={3}>
                    <TextField
                      value={model && model[idKey] != null ? model[idKey] : ""}
                      label="ID"
                      fullWidth
                      size="small"
                      disabled
                    />
                  </Grid>
                ) : null}

                {(usaTabs
                  ? fields.filter(
                      (f) => (f.tab || tabsNames[0]) === tabsNames[tab]
                    )
                  : fields
                ).map((f) => renderField(f))}
              </Grid>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button onClick={onHide} color="inherit" startIcon={<CloseIcon />}>
                Cancelar
              </Button>
              <Button
                onClick={aceptarClick}
                variant="contained"
                startIcon={<CheckIcon />}
              >
                Guardar
              </Button>
            </DialogActions>
          </Dialog>
        </ThemeProvider>
      ) : null}
    </Fragment>
  );
}
